import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  Target, 
  Search,
  Plus, 
  Edit3, 
  Trash2, 
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Tag,
  DollarSign,
  Filter,
  FileText,
  Zap,
  Archive
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const IRDBPPoolManager = ({ calculationId, trialBalanceAccounts = [] }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("ird");

  const [irdPools, setIrdPools] = useState([]);
  const [bpPools, setBpPools] = useState([]);
  const [taggedAccounts, setTaggedAccounts] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterPool, setFilterPool] = useState("all");
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [showTaggingForm, setShowTaggingForm] = useState(false);
  
  const [taggingForm, setTaggingForm] = useState({
    account_id: "",
    account_name: "",
    account_number: "",
    original_amount: "",
    tagged_pools: [],
    ird_allocation: "100",
    bp_allocation: "0",
    tag_reason: "",
    allowability_status: "allowable",
    allowability_notes: ""
  });

  useEffect(() => {
    if (calculationId) {
      loadPoolData();
      loadTaggedAccounts();
    }
  }, [calculationId]);

  const loadPoolData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const irdResponse = await axios.get(`${API}/ird-pools/${calculationId}`);
      setIrdPools(irdResponse.data.ird_pools || []);

      const bpResponse = await axios.get(`${API}/bp-pools/${calculationId}`);
      setBpPools(bpResponse.data.bp_pools || []);
    } catch (e) {
      if (e.response?.status !== 404) {
        setError(e.response?.data?.detail || "Error loading pool data");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTaggedAccounts = async () => {
    try {
      const response = await axios.get(`${API}/tagged-accounts/${calculationId}`);
      setTaggedAccounts(response.data.tagged_accounts || []);
    } catch (e) {
      if (e.response?.status !== 404) {
        console.warn("Could not load tagged accounts:", e.message);
      }
    }
  };

  const generateConsistentAccountId = (account) => {
    const accountParts = account.account ? account.account.split(' - ') : ['', ''];
    const accountNumber = accountParts[0] || '';
    const accountName = accountParts[1] || account.account || '';
    return `${accountNumber}_${accountName.replace(/[^a-zA-Z0-9]/g, '')}_${Math.abs(account.net_amount || 0).toString().replace('.', '')}`;
  };

  const handleTagAccount = (account) => {
    const accountParts = account.account ? account.account.split(' - ') : ['', ''];
    const accountNumber = accountParts[0] || '';
    const accountName = accountParts[1] || account.account || '';
    
    setTaggingForm({
      account_id: generateConsistentAccountId(account),
      account_name: accountName,
      account_number: accountNumber,
      original_amount: account.net_amount || account.amount || account.balance || 0,
      tagged_pools: [],
      ird_allocation: "100",
      bp_allocation: "0",
      tag_reason: "",
      allowability_status: "allowable",
      allowability_notes: ""
    });
    setShowTaggingForm(true);
  };

  const handleSaveTagging = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    if (!taggingForm.account_id || !taggingForm.account_name) {
      setError("Account ID and name are required");
      setSaving(false);
      return;
    }

    const irdAllocation = parseFloat(taggingForm.ird_allocation) / 100;
    const bpAllocation = parseFloat(taggingForm.bp_allocation) / 100;

    if (Math.abs((irdAllocation + bpAllocation) - 1) > 0.01) {
      setError("IR&D and B&P allocations must total 100%");
      setSaving(false);
      return;
    }

    const taggedPools = [];
    const poolAllocations = {};

    if (irdAllocation > 0) {
      taggedPools.push("ird");
      poolAllocations["ird"] = irdAllocation;
    }
    if (bpAllocation > 0) {
      taggedPools.push("bp");
      poolAllocations["bp"] = bpAllocation;
    }

    try {
      const taggingData = {
        calculation_id: calculationId,
        account_id: taggingForm.account_id,
        account_name: taggingForm.account_name,
        account_number: taggingForm.account_number || null,
        original_amount: parseFloat(taggingForm.original_amount) || 0,
        tagged_pools: taggedPools,
        pool_allocations: poolAllocations,
        tagged_by: "user",
        tag_reason: taggingForm.tag_reason || null,
        allowability_status: taggingForm.allowability_status,
        allowability_notes: taggingForm.allowability_notes || null
      };

      const response = await axios.post(`${API}/tag-account`, taggingData);

      if (response.data.success) {
        setSuccess("Account tagged successfully!");
        setShowTaggingForm(false);
        loadTaggedAccounts();
        loadPoolData();
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error tagging account");
    } finally {
      setSaving(false);
    }
  };

  const handleUntagAccount = async (accountId) => {
    if (!window.confirm("Are you sure you want to remove this account tagging?")) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/untag-account/${calculationId}/${accountId}`);
      if (response.data.success) {
        setSuccess("Account untagged successfully!");
        loadTaggedAccounts();
        loadPoolData();
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error untagging account");
    }
  };

  const handleCreatePool = async (poolType) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const poolData = {
        pool_name: poolType === "ird" ? "IR&D Pool" : "B&P Pool",
        pool_code: poolType === "ird" ? "IRD" : "BP",
        tagged_account_ids: [],
        tagged_accounts_detail: [],
        total_pool_costs: 0.0,
        direct_costs: 0.0,
        allocated_costs: 0.0,
        allowable_costs: 0.0,
        unallowable_costs: 0.0,
        allocation_method: "percentage",
        allocation_targets: [
          { pool_id: "G&A", percentage: poolType === "ird" ? 60 : 70 },
          { pool_id: "OH", percentage: poolType === "ird" ? 40 : 30 }
        ],
        allocation_base: "cost_input",
        calculation_id: calculationId
      };

      const endpoint = poolType === "ird" ? "ird-pools" : "bp-pools";
      const response = await axios.post(`${API}/${endpoint}`, poolData);

      if (response.data.success) {
        setSuccess(`${poolType.toUpperCase()} pool created successfully!`);
        loadPoolData();
      } else {
        setError(`Failed to create ${poolType.toUpperCase()} pool`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || `Error creating ${poolType.toUpperCase()} pool`);
    } finally {
      setSaving(false);
    }
  };

  const getPoolSummary = (poolType) => {
    const pools = poolType === "ird" ? irdPools : bpPools;
    const pool = pools.length > 0 ? pools[0] : null;
    
    if (!pool) {
      return {
        total_costs: 0,
        direct_costs: 0,
        allocated_costs: 0,
        allowable_costs: 0,
        unallowable_costs: 0,
        tagged_accounts_count: 0
      };
    }

    return {
      total_costs: pool.total_pool_costs || 0,
      direct_costs: pool.direct_costs || 0,
      allocated_costs: pool.allocated_costs || 0,
      allowable_costs: pool.allowable_costs || 0,
      unallowable_costs: pool.unallowable_costs || 0,
      tagged_accounts_count: pool.tagged_account_ids?.length || 0
    };
  };

  const getFilteredAccounts = () => {
    if (!trialBalanceAccounts || trialBalanceAccounts.length === 0) {
      return [];
    }

    let filtered = trialBalanceAccounts;

    if (searchTerm) {
      filtered = filtered.filter(account => {
        const accountText = account.account || '';
        return accountText.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    if (filterPool !== "all") {
      filtered = filtered.filter(account => {
        const accountId = account.id || `${account.account}_${Date.now()}`;
        if (filterPool === "tagged") {
          return taggedAccounts.some(tagged => tagged.account_id === accountId);
        } else if (filterPool === "untagged") {
          return !taggedAccounts.some(tagged => tagged.account_id === accountId);
        }
        return true;
      });
    }

    return filtered;
  };

  const getTaggedAccountInfo = (accountId) => {
    return taggedAccounts.find(tagged => tagged.account_id === accountId);
  };

  const formatCurrency = (value) => {
    return value ? `$${Number(value).toLocaleString()}` : '$0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading IR&D and B&P pools...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-green-700">{success}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="ird" className="flex items-center space-x-2">
            <Target className="h-4 w-4" />
            <span>IR&D Pool</span>
          </TabsTrigger>
          <TabsTrigger value="bp" className="flex items-center space-x-2">
            <FileText className="h-4 w-4" />
            <span>B&P Pool</span>
          </TabsTrigger>
          <TabsTrigger value="tagging" className="flex items-center space-x-2">
            <Tag className="h-4 w-4" />
            <span>Account Tagging</span>
          </TabsTrigger>
        </TabsList>

        {/* IR&D Pool Tab */}
        <TabsContent value="ird" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Target className="h-5 w-5 text-blue-600" />
                    <span>IR&D Pool Summary</span>
                    <Badge variant="outline">Independent Research &amp; Development</Badge>
                  </CardTitle>
                </div>
                {irdPools.length === 0 && (
                  <Button onClick={() => handleCreatePool("ird")} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="h-4 w-4 mr-2" />
                    {saving ? "Creating..." : "Create IR&D Pool"}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const summary = getPoolSummary("ird");
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-blue-900">Total Pool Costs</div>
                      <div className="text-2xl font-bold text-blue-600">{formatCurrency(summary.total_costs)}</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-green-900">Allowable Costs</div>
                      <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.allowable_costs)}</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-blue-900">Tagged Accounts</div>
                      <div className="text-2xl font-bold text-blue-600">{summary.tagged_accounts_count}</div>
                    </div>
                  </div>
                );
              })()}

              <Separator className="my-6" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3 text-slate-700">Cost Breakdown</h4>
                  {(() => {
                    const summary = getPoolSummary("ird");
                    return (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span>Direct Costs:</span><span className="font-medium">{formatCurrency(summary.direct_costs)}</span></div>
                        <div className="flex justify-between"><span>Allocated Costs:</span><span className="font-medium">{formatCurrency(summary.allocated_costs)}</span></div>
                        <div className="flex justify-between border-t pt-2"><span>Total Costs:</span><span className="font-bold">{formatCurrency(summary.total_costs)}</span></div>
                        <Separator />
                        <div className="flex justify-between text-red-600"><span>Unallowable Costs:</span><span className="font-medium">{formatCurrency(summary.unallowable_costs)}</span></div>
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <h4 className="font-semibold mb-3 text-slate-700">Tagged Accounts</h4>
                  <div className="space-y-2">
                    {taggedAccounts.filter(account => account.tagged_pools.includes("ird")).slice(0, 5).map((account, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="truncate flex-1">{account.account_name}</span>
                        <span className="font-medium ml-2">{formatCurrency(account.original_amount * (account.pool_allocations?.ird || 1))}</span>
                      </div>
                    ))}
                    {taggedAccounts.filter(account => account.tagged_pools.includes("ird")).length === 0 && (
                      <p className="text-sm text-slate-500 italic">No accounts tagged to IR&D pool yet</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* B&P Pool Tab */}
        <TabsContent value="bp" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-green-600" />
                    <span>B&P Pool Summary</span>
                    <Badge variant="outline">Bid &amp; Proposal</Badge>
                  </CardTitle>
                </div>
                {bpPools.length === 0 && (
                  <Button onClick={() => handleCreatePool("bp")} disabled={saving} className="bg-green-600 hover:bg-green-700">
                    <Plus className="h-4 w-4 mr-2" />
                    {saving ? "Creating..." : "Create B&P Pool"}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const summary = getPoolSummary("bp");
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-green-900">Total Pool Costs</div>
                      <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_costs)}</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-blue-900">Allowable Costs</div>
                      <div className="text-2xl font-bold text-blue-600">{formatCurrency(summary.allowable_costs)}</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-blue-900">Tagged Accounts</div>
                      <div className="text-2xl font-bold text-blue-600">{summary.tagged_accounts_count}</div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Account Tagging Tab */}
        <TabsContent value="tagging" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Tag className="h-5 w-5 text-indigo-600" />
                <span>Tag Accounts to Pools</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Accounts</h4>
                  <div className="mb-3 flex gap-2">
                    <Input placeholder="Search accounts" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    <Select value={filterPool} onValueChange={setFilterPool}>
                      <SelectTrigger className="w-[140px]"><SelectValue placeholder="Filter" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="tagged">Tagged</SelectItem>
                        <SelectItem value="untagged">Untagged</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-md border divide-y max-h-96 overflow-y-auto">
                    {getFilteredAccounts().map((acc, idx) => {
                      const accountId = acc.id || generateConsistentAccountId(acc)
                      const info = getTaggedAccountInfo(accountId)
                      return (
                        <div key={idx} className="p-3 flex items-center justify-between hover:bg-slate-50">
                          <div className="mr-2 min-w-0">
                            <div className="truncate font-medium text-sm">{acc.account}</div>
                            {info && (<div className="text-xs text-slate-500">Tagged: {info.tagged_pools.join(', ')}</div>)}
                          </div>
                          <div className="flex gap-2">
                            {!info ? (
                              <Button size="sm" onClick={() => handleTagAccount(acc)}>Tag</Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => handleUntagAccount(info.account_id)}>Untag</Button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold mb-3">Tagging Form</h4>
                  {!showTaggingForm ? (
                    <Alert className="border-yellow-200 bg-yellow-50">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>Select an account to tag to IR&D/B&P pools.</AlertDescription>
                    </Alert>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Account Name</Label>
                          <Input value={taggingForm.account_name} onChange={(e) => setTaggingForm(prev => ({ ...prev, account_name: e.target.value }))} />
                        </div>
                        <div>
                          <Label>Account Number</Label>
                          <Input value={taggingForm.account_number} onChange={(e) => setTaggingForm(prev => ({ ...prev, account_number: e.target.value }))} />
                        </div>
                      </div>
                      <div>
                        <Label>Original Amount</Label>
                        <Input type="number" value={taggingForm.original_amount} onChange={(e) => setTaggingForm(prev => ({ ...prev, original_amount: e.target.value }))} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>IR&amp;D Allocation (%)</Label>
                          <Input type="number" value={taggingForm.ird_allocation} onChange={(e) => setTaggingForm(prev => ({ ...prev, ird_allocation: e.target.value }))} />
                        </div>
                        <div>
                          <Label>B&amp;P Allocation (%)</Label>
                          <Input type="number" value={taggingForm.bp_allocation} onChange={(e) => setTaggingForm(prev => ({ ...prev, bp_allocation: e.target.value }))} />
                        </div>
                      </div>
                      <div>
                        <Label>Allowability Status</Label>
                        <Select value={taggingForm.allowability_status} onValueChange={(v) => setTaggingForm(prev => ({ ...prev, allowability_status: v }))}>
                          <SelectTrigger className="w/full"><SelectValue placeholder="Select status" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="allowable">Allowable</SelectItem>
                            <SelectItem value="unallowable">Unallowable</SelectItem>
                            <SelectItem value="requires_review">Requires Review</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Tag Reason</Label>
                        <Textarea rows={3} value={taggingForm.tag_reason} onChange={(e) => setTaggingForm(prev => ({ ...prev, tag_reason: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Notes</Label>
                        <Textarea rows={3} value={taggingForm.allowability_notes} onChange={(e) => setTaggingForm(prev => ({ ...prev, allowability_notes: e.target.value }))} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowTaggingForm(false)}>Cancel</Button>
                        <Button onClick={handleSaveTagging} disabled={saving}>Save Tag</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IRDBPPoolManager;